import React, { useState } from 'react';
import '../styles/sms.css';

const SMSNotifications = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'alert',
      message: 'Severe weather warning: Heavy rainfall expected in the next 48 hours. Please secure your crops and equipment.',
      recipients: 'All Farmers',
      status: 'sent',
      sentAt: '2023-06-15T09:30:00',
      responses: [
        { id: 1, sender: 'John Smith', message: 'Thanks for the heads up. Will prepare accordingly.', time: '2023-06-15T10:15:00' },
        { id: 2, sender: 'Maria Garcia', message: 'Already securing the greenhouse. Thanks!', time: '2023-06-15T10:45:00' }
      ]
    },
    {
      id: 2,
      type: 'update',
      message: 'New sustainable farming workshop scheduled for July 15th at the Community Center. Registration now open.',
      recipients: 'Organic Farmers',
      status: 'sent',
      sentAt: '2023-06-10T14:00:00',
      responses: []
    },
    {
      id: 3,
      type: 'reminder',
      message: 'Crop rotation reports due by end of week. Please submit through the portal or contact your local office.',
      recipients: 'All Farmers',
      status: 'pending',
      scheduledFor: '2023-06-20T08:00:00',
      responses: []
    },
    {
      id: 4,
      type: 'alert',
      message: 'Pest outbreak detected in the northern region. Recommended preventative measures have been sent to your email.',
      recipients: 'Northern Region',
      status: 'failed',
      sentAt: '2023-06-05T11:15:00',
      responses: []
    }
  ]);

  const [templates, setTemplates] = useState([
    { id: 1, name: 'Weather Alert', type: 'alert', content: 'Weather alert: [DETAILS]. Take necessary precautions for your crops.' },
    { id: 2, name: 'Workshop Invite', type: 'update', content: 'New workshop: [TITLE] on [DATE] at [LOCATION]. Register now!' },
    { id: 3, name: 'Report Reminder', type: 'reminder', content: 'Reminder: [REPORT_TYPE] reports due by [DATE]. Submit through the portal.' },
    { id: 4, name: 'Pest Warning', type: 'alert', content: 'Pest alert: [PEST_TYPE] detected in [REGION]. Check your crops and apply recommended treatments.' }
  ]);

  const [groups, setGroups] = useState([
    { id: 1, name: 'All Farmers', count: 156 },
    { id: 2, name: 'Organic Farmers', count: 48 },
    { id: 3, name: 'Northern Region', count: 37 },
    { id: 4, name: 'Southern Region', count: 42 },
    { id: 5, name: 'Eastern Region', count: 31 },
    { id: 6, name: 'Western Region', count: 46 },
    { id: 7, name: 'New Farmers', count: 23 },
    { id: 8, name: 'Experienced Farmers', count: 89 }
  ]);

  const [newMessage, setNewMessage] = useState({
    message: '',
    recipients: [],
    type: 'update',
    scheduled: false,
    scheduledDate: '',
    scheduledTime: ''
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'var(--accent-green)';
      case 'pending':
        return 'var(--accent-orange)';
      case 'failed':
        return 'var(--accent-red)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'alert':
        return '⚠️';
      case 'update':
        return '📢';
      case 'reminder':
        return '🔔';
      default:
        return '📱';
    }
  };

  const handleMessageChange = (e) => {
    setNewMessage({ ...newMessage, message: e.target.value });
  };

  const handleTypeChange = (e) => {
    setNewMessage({ ...newMessage, type: e.target.value });
  };

  const handleTemplateSelect = (template) => {
    setNewMessage({ ...newMessage, message: template.content, type: template.type });
  };

  const handleRecipientToggle = (group) => {
    const isSelected = newMessage.recipients.includes(group.name);
    
    if (isSelected) {
      setNewMessage({
        ...newMessage,
        recipients: newMessage.recipients.filter(name => name !== group.name)
      });
    } else {
      setNewMessage({
        ...newMessage,
        recipients: [...newMessage.recipients, group.name]
      });
    }
  };

  const handleScheduleToggle = (e) => {
    setNewMessage({ ...newMessage, scheduled: e.target.checked });
  };

  const handleDateChange = (e) => {
    setNewMessage({ ...newMessage, scheduledDate: e.target.value });
  };

  const handleTimeChange = (e) => {
    setNewMessage({ ...newMessage, scheduledTime: e.target.value });
  };

  const handleSendMessage = () => {
    const newNotification = {
      id: notifications.length + 1,
      type: newMessage.type,
      message: newMessage.message,
      recipients: newMessage.recipients.join(', '),
      status: newMessage.scheduled ? 'pending' : 'sent',
      sentAt: newMessage.scheduled ? null : new Date().toISOString(),
      scheduledFor: newMessage.scheduled ? `${newMessage.scheduledDate}T${newMessage.scheduledTime}:00` : null,
      responses: []
    };

    setNotifications([newNotification, ...notifications]);
    setNewMessage({
      message: '',
      recipients: [],
      type: 'update',
      scheduled: false,
      scheduledDate: '',
      scheduledTime: ''
    });
  };

  const isMessageValid = newMessage.message.trim() !== '' && newMessage.recipients.length > 0;
  const isScheduleValid = !newMessage.scheduled || (newMessage.scheduledDate !== '' && newMessage.scheduledTime !== '');

  return (
    <div className="sms-notifications">
      <div className="section-header">
        <h2>SMS Notifications</h2>
      </div>
      
      <div className="sms-container">
        <div className="sms-history">
          <h3>Notification History</h3>
          <div className="notifications-list">
            {notifications.map(notification => (
              <div 
                key={notification.id} 
                className="notification-card" 
                data-type={notification.type}
              >
                <div className="notification-icon">
                  {getTypeIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <p className="notification-message">{notification.message}</p>
                  <div className="notification-meta">
                    <span className="notification-recipients">To: {notification.recipients}</span>
                    {notification.sentAt && (
                      <span className="notification-sent">Sent: {new Date(notification.sentAt).toLocaleString()}</span>
                    )}
                    {notification.scheduledFor && (
                      <span className="notification-scheduled">Scheduled: {new Date(notification.scheduledFor).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <div 
                  className="notification-status" 
                  style={{ backgroundColor: `${getStatusColor(notification.status)}20`, color: getStatusColor(notification.status) }}
                >
                  {notification.status}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="sms-composer">
          <h3>Compose Message</h3>
          
          <div className="composer-section">
            <label>Message Type</label>
            <div className="message-type-selector">
              <select value={newMessage.type} onChange={handleTypeChange}>
                <option value="update">Update</option>
                <option value="alert">Alert</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>
          </div>
          
          <div className="composer-section">
            <label>Message</label>
            <textarea 
              value={newMessage.message} 
              onChange={handleMessageChange} 
              placeholder="Type your message here..."
            />
          </div>
          
          <div className="composer-section">
            <label>Templates</label>
            <div className="templates-list">
              {templates.map(template => (
                <div 
                  key={template.id} 
                  className="template-item" 
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="template-icon">{getTypeIcon(template.type)}</div>
                  <span className="template-name">{template.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="composer-section">
            <label>Recipients</label>
            <div className="recipients-container">
              <div className="selected-recipients">
                {newMessage.recipients.length > 0 ? (
                  newMessage.recipients.map((recipient, index) => (
                    <div key={index} className="recipient-tag">
                      <span>{recipient}</span>
                      <button 
                        className="remove-recipient" 
                        onClick={() => handleRecipientToggle({ name: recipient })}
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <span className="no-recipients" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                    No recipients selected
                  </span>
                )}
              </div>
              
              <div className="recipient-groups">
                <h4>Recipient Groups</h4>
                <div className="groups-list">
                  {groups.map(group => (
                    <div 
                      key={group.id} 
                      className="group-item" 
                      onClick={() => handleRecipientToggle(group)}
                      style={newMessage.recipients.includes(group.name) ? { 
                        backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                        borderColor: 'var(--accent-blue)' 
                      } : {}}
                    >
                      <span className="group-icon">👥</span>
                      <span className="group-name">{group.name}</span>
                      <span className="group-count">{group.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="composer-section">
            <div className="schedule-toggle">
              <input 
                type="checkbox" 
                id="scheduleToggle" 
                checked={newMessage.scheduled} 
                onChange={handleScheduleToggle} 
              />
              <label htmlFor="scheduleToggle">Schedule for later</label>
            </div>
            
            {newMessage.scheduled && (
              <div className="schedule-inputs">
                <input 
                  type="date" 
                  value={newMessage.scheduledDate} 
                  onChange={handleDateChange} 
                  min={new Date().toISOString().split('T')[0]} 
                />
                <input 
                  type="time" 
                  value={newMessage.scheduledTime} 
                  onChange={handleTimeChange} 
                />
              </div>
            )}
          </div>
          
          <div className="composer-actions">
            <button 
              className="send-button" 
              onClick={handleSendMessage} 
              disabled={!isMessageValid || !isScheduleValid}
            >
              {newMessage.scheduled ? 'Schedule Message' : 'Send Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSNotifications; 